import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Clock, MapPin, Send } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { useCaptcha, HumanCheck } from "@/components/HumanCheck";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AREAS } from "@/data/areas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Mail, label: "Email", value: "info@vortexvs.com", href: "mailto:info@vortexvs.com", testid: "contact-info-email" },
  { icon: Phone, label: "Phone", value: "(610) 983-8060", href: "tel:+16109838060", testid: "contact-info-phone" },
  { icon: Clock, label: "Response", value: "Within one business day", testid: "contact-info-response" },
  { icon: MapPin, label: "Service Area", value: "West Chester, PA + ~20 miles — western Philly suburbs", testid: "contact-info-area" },
];

const TOWNS = AREAS.map((a) => a.name);
const LOCATION_TYPES = ["Office", "Apartment building", "Medical office", "Other"];
const HEADCOUNTS = ["Under 25", "25–50", "50–100", "100–250", "250+"];
const EMPTY = { name: "", email: "", phone: "", location_type: "", headcount: "", subject: "", message: "", company: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const captcha = useCaptcha();

  useEffect(() => { captcha.checkRequired(); }, [captcha.checkRequired]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, ...captcha.fields });
      toast.success("Message sent — we will get back to you within one business day.");
      setForm(EMPTY);
      captcha.load().catch(() => {});
    } catch (err) {
      const rejected = await captcha.handleReject(err);
      if (rejected === "required") {
        toast.info("Quick human check — answer the question to send.");
      } else if (rejected === "wrong") {
        toast.error("Wrong answer — try the new question.");
      } else {
        toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pt-36 pb-24 lg:pt-48 lg:pb-36">
        <div aria-hidden="true" className="absolute -top-32 left-1/3 h-[26rem] w-[26rem] rounded-full bg-vortex-blue/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.1}>
            <Overline>Contact Us</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl" data-testid="contact-heading">
            <MaskedLine delay={0.25}>TALK TO A HUMAN.</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-vortex-cyan">TODAY.</span>
            </MaskedLine>
          </h1>

          <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-12">
            <Reveal delay={0.5} className="lg:col-span-5">
              <p className="max-w-md text-base leading-relaxed text-slate-400">
                Want a smart cooler at your location, or have a question about an
                existing one? Fast communication is our specialty — reach out and
                watch how quickly we answer.
              </p>
              <div className="mt-12 space-y-2">
                {INFO.map((item) => (
                  <div
                    key={item.label}
                    className="group flex items-center gap-6 border-t border-white/10 py-6 transition-colors duration-300 hover:border-vortex-cyan/30"
                    data-testid={item.testid}
                  >
                    <item.icon size={20} className="shrink-0 text-vortex-cyan" />
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="mt-1 block text-base text-slate-200 transition-colors hover:text-vortex-cyan">
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-base text-slate-200">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="border-t border-white/10" />
              </div>
              <div className="mt-10" data-testid="service-towns">
                <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Towns we serve</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {TOWNS.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/10 px-3.5 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-400 transition-colors duration-300 hover:border-vortex-cyan/40 hover:text-vortex-cyan"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">Just outside the list? Ask anyway — we are growing.</p>
              </div>
            </Reveal>

            <Reveal delay={0.6} className="lg:col-span-7">
              <form
                onSubmit={submit}
                className="border border-white/10 bg-vortex-surface/60 p-8 backdrop-blur-xl glow-cyan lg:p-12"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Name</Label>
                    <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="Your name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Email</Label>
                    <Input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="you@company.com" />
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Phone (optional)</Label>
                    <Input id="contact-phone" data-testid="contact-phone-input" type="tel" autoComplete="tel" maxLength={30} value={form.phone} onChange={set("phone")}
                      className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="(610) 555-0123" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Location type</Label>
                    <Select value={form.location_type} onValueChange={(v) => setForm({ ...form, location_type: v })}>
                      <SelectTrigger data-testid="contact-location-type-select" className="border-white/10 bg-vortex-ink/60 text-slate-100 focus:ring-vortex-cyan">
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-vortex-surface text-slate-100">
                        {LOCATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">People on-site</Label>
                    <Select value={form.headcount} onValueChange={(v) => setForm({ ...form, headcount: v })}>
                      <SelectTrigger data-testid="contact-headcount-select" className="border-white/10 bg-vortex-ink/60 text-slate-100 focus:ring-vortex-cyan">
                        <SelectValue placeholder="Approximate" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-vortex-surface text-slate-100">
                        {HEADCOUNTS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-6 space-y-2">
                  <Label htmlFor="contact-subject" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Subject</Label>
                  <Input id="contact-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")}
                    className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="I want a smart cooler at my office" />
                </div>
                <div className="mt-6 space-y-2">
                  <Label htmlFor="contact-message" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Message</Label>
                  <Textarea id="contact-message" maxLength={5000} data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")}
                    className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan" placeholder="Tell us about your location and what you would like stocked..." />
                </div>
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="contact-honeypot" style={{ position: "absolute", left: "-9999px", opacity: 0 }} />
                <HumanCheck captcha={captcha.captcha} answer={captcha.answer} setAnswer={captcha.setAnswer} onRefresh={captcha.load} />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(92,179,226,0.5)] disabled:opacity-50 sm:w-auto"
                >
                  {sending ? "Sending..." : "Send message"}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
